export const caseStatuses = [
  "draft",
  "submitted",
  "initial_review",
  "correction_required",
  "correction_submitted",
  "admitted",
  "assigned",
  "in_progress",
  "resolved",
  "rejected",
  "archived",
  "cancelled",
] as const;

export type CaseStatus = (typeof caseStatuses)[number];

export const userRoles = [
  "super_admin",
  "municipal_admin",
  "mayor",
  "general_secretary",
  "citizen",
  "student",
  "intake_agent",
  "intake_supervisor",
  "system_admin",
  "department_agent",
  "department_head",
  "information_officer",
  "treasury_agent",
  "auditor",
  "executive_viewer",
  "security_officer",
  "fleet_operator",
  "designer",
  "news_editor",
  "academic_admin",
  "academic_editor",
] as const;

export type UserRole = (typeof userRoles)[number];

export type CaseAction =
  | "save_draft"
  | "submit"
  | "take"
  | "release"
  | "request_correction"
  | "submit_correction"
  | "admit"
  | "reject"
  | "assign"
  | "start_processing"
  | "resolve"
  | "archive"
  | "cancel";

export type Modality = "online" | "presencial" | "mixta";
export type CostKind = "gratuito" | "variable" | "con_tasa";

export type PaymentObligationStatus =
  "pending" | "partially_paid" | "paid" | "cancelled" | "expired";

export type PaymentTransactionStatus =
  "submitted" | "verified" | "rejected" | "reversed";

export type PaymentChannel = "cashier" | "bank_transfer" | "government_button";

export interface PaymentObligation {
  id: string;
  municipalityId: string;
  citizenId: string;
  citizenName: string;
  referenceCode: string;
  concept: string;
  description: string;
  amountGuarani: number;
  amountPaidGuarani: number;
  dueOn: string | null;
  status: PaymentObligationStatus;
  createdAt: string;
}

export interface PaymentTransaction {
  id: string;
  municipalityId: string;
  obligationId: string;
  citizenId: string;
  channel: PaymentChannel;
  status: PaymentTransactionStatus;
  amountGuarani: number;
  externalReference: string;
  proofStoragePath: string | null;
  submittedBy: string;
  submittedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string;
  receiptNumber: string | null;
  receiptIssuedAt: string | null;
}

export interface Department {
  id: string;
  name: string;
  shortName: string;
  description: string;
  location: string;
  schedule: string;
  phone: string;
  email: string;
}

export interface ProcedureRequirement {
  id: string;
  label: string;
  labelGn?: string;
  description: string;
  descriptionGn?: string;
  required: boolean;
}

export interface Procedure {
  id: string;
  slug: string;
  name: string;
  nameGn?: string;
  summary: string;
  summaryGn?: string;
  audience: string;
  audienceGn?: string;
  category: string;
  categoryGn?: string;
  modality: Modality;
  costKind: CostKind;
  costLabel: string;
  costLabelGn?: string;
  estimatedTime: string;
  estimatedTimeGn?: string;
  departmentId: string;
  accountRequired: boolean;
  digitalStatus: "operativo" | "consulta" | "preliminar";
  requirements: ProcedureRequirement[];
  steps: string[];
  stepsGn?: string[];
  normativeBasis: string;
  normativeBasisGn?: string;
  faqs: Array<{ question: string; answer: string }>;
  faqsGn?: Array<{ question: string; answer: string }>;
  helpChannel: string;
  helpChannelGn?: string;
  lastReviewedAt: string;
  contentOwner: string;
  contentOwnerGn?: string;
}

export interface NewsItem {
  id: string;
  slug: string;
  title: string;
  titleGn: string;
  excerpt: string;
  excerptGn: string;
  body: string;
  bodyGn: string;
  category: string;
  categoryGn: string;
  publishedAt: string;
  importance: number;
  imageUrl: string | null;
  imageAlt: string;
  imageAltGn: string;
  imageCredit: string;
  sourceUrl: string | null;
  relatedFiles: string[];
}

export interface SiteMedia {
  id: string;
  slotCode: string;
  label: string;
  labelGn: string;
  storagePath: string;
  imageUrl: string;
  altText: string;
  altTextGn: string;
  credit: string;
  active: boolean;
  description: string;
  descriptionGn: string;
  displayOrder: number;
  focalX: number;
  focalY: number;
}

export interface MayorProfile {
  id: string;
  fullName: string;
  officeTitle: string;
  officeTitleGn: string;
  profileTitle: string;
  profileTitleGn: string;
  biography: string;
  biographyGn: string;
  institutionalDescription: string;
  institutionalDescriptionGn: string;
  published: boolean;
}

export interface CouncilMember {
  id: string;
  fullName: string;
  positionTitle: string;
  positionTitleGn: string;
  affiliation: string;
  affiliationGn: string;
  displayOrder: number;
  published: boolean;
  officiallyProclaimed: boolean;
  sourceUrl: string | null;
  sourceLabel: string;
  sourceLabelGn: string;
  sourceCheckedOn: string | null;
  imagePath: string | null;
  imageUrl: string | null;
  imageAlt: string;
  imageAltGn: string;
  imageCredit: string;
}

export interface PublicPageContent {
  id: string;
  pageCode: "institution" | "tourism";
  eyebrow: string;
  eyebrowGn: string;
  title: string;
  titleGn: string;
  summary: string;
  summaryGn: string;
  body: string;
  bodyGn: string;
  published: boolean;
}

export type TouristAttractionCategory =
  "naturaleza" | "historia" | "cultura" | "circuito";

export interface TouristAttraction {
  id: string;
  slug: string;
  name: string;
  nameGn: string;
  category: TouristAttractionCategory;
  shortDescription: string;
  shortDescriptionGn: string;
  location: string;
  locationGn: string;
  mapUrl: string | null;
  latitude: number | null;
  longitude: number | null;
  imagePath: string | null;
  imageUrl: string | null;
  imageAlt: string;
  imageAltGn: string;
  imageCredit: string;
  sourceUrl: string | null;
  displayOrder: number;
  published: boolean;
}

export interface WorkItem {
  id: string;
  title: string;
  description: string;
  location: string;
  status: "planificada" | "en_ejecucion" | "finalizada";
  budget: string;
  funding: string;
  progress: number;
  startDate: string;
  endDate: string;
  contractor: string;
  documents: string[];
}

export interface TransparencyDocument {
  id: string;
  title: string;
  category: string;
  period: string;
  publishedAt: string;
  updatedAt: string;
  department: string;
  format: "PDF" | "XLSX" | "CSV";
  size: string;
  status: "vigente" | "histórico";
}

export interface CaseDocument {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  version: number;
  visibility: "citizen" | "internal";
  uploadedAt: string;
  uploadedBy: string;
  correctionId: string | null;
}

export interface CaseEvent {
  id: string;
  action: CaseAction | "priority_changed" | "internal_note" | "created" | "reassigned";
  fromStatus: CaseStatus | null;
  toStatus: CaseStatus;
  actorId: string;
  actorLabel: string;
  createdAt: string;
  reason: string;
  visibility: "citizen" | "internal";
  departmentId: string | null;
  documentId: string | null;
}

export interface CorrectionRequest {
  id: string;
  version: number;
  requestedAt: string;
  requestedBy: string;
  details: string;
  status: "pending" | "answered" | "accepted";
  answeredAt: string | null;
  answer: string | null;
  documentIds: string[];
}

export interface MunicipalCase {
  id: string;
  number: string | null;
  citizenId: string;
  procedureId: string;
  applicantType: "persona" | "organizacion";
  applicantName: string;
  documentMasked: string;
  email: string;
  phone: string;
  subject: string;
  category: string;
  suggestedDepartmentId: string;
  currentDepartmentId: string | null;
  description: string;
  relatedAddress: string;
  representsAnother: boolean;
  representedName: string;
  status: CaseStatus;
  priority: "baja" | "normal" | "alta" | "urgente";
  assignedTo: string | null;
  assignedToName: string | null;
  createdAt: string;
  submittedAt: string | null;
  updatedAt: string;
  dueAt: string | null;
  declarationAccepted: boolean;
  privacyAccepted: boolean;
  documents: CaseDocument[];
  corrections: CorrectionRequest[];
  events: CaseEvent[];
  internalNotes: Array<{
    id: string;
    text: string;
    author: string;
    createdAt: string;
  }>;
}

export interface CaseDraftInput {
  id?: string;
  applicantType: "persona" | "organizacion";
  applicantName: string;
  document: string;
  email: string;
  phone: string;
  subject: string;
  category: string;
  suggestedDepartmentId: string;
  description: string;
  relatedAddress: string;
  representsAnother: boolean;
  representedName: string;
  declarationAccepted: boolean;
  privacyAccepted: boolean;
  files: File[];
}

export interface UserIdentity {
  id: string;
  email: string;
  name: string;
  role: UserRole;
}

export interface SessionUser extends UserIdentity {
  source: "supabase";
  roles: UserRole[];
  municipalityId: string | null;
}

export type AccessScope = "municipality" | "department" | "own";

export interface MunicipalPosition {
  id: string;
  municipalityId: string;
  departmentId: string | null;
  code: string;
  name: string;
  description: string;
  responsibilities: string[];
  leadership: boolean;
  active: boolean;
}

export interface StaffAssignment {
  id: string;
  userId: string;
  fullName: string;
  departmentId: string;
  departmentName: string;
  positionId: string;
  positionName: string;
  roleId: string;
  roleCode: string;
  roleName: string;
  functionSummary: string;
  responsibilities: string[];
  authorityLimit: string;
  primary: boolean;
  status: "active" | "leave" | "ended";
  startsOn: string;
  endsOn: string | null;
}

export interface MunicipalModuleSetting {
  code: string;
  area: "public" | "citizen" | "management";
  name: string;
  description: string;
  route: string;
  critical: boolean;
  displayOrder: number;
  enabled: boolean;
  maintenanceMessage: string;
}

export interface RolePermissionSummary {
  roleId: string;
  roleCode: string;
  roleName: string;
  permissionCode: string;
  permissionName: string;
  riskLevel: "low" | "normal" | "high" | "critical";
  scope: AccessScope;
  effect: "allow" | "deny";
}

export interface ExecutiveDashboardSnapshot {
  periodDays: 7 | 30 | 90 | 365;
  generatedAt: string;
  portal: {
    pageViews: number;
    previousPageViews: number;
    uniqueVisitors: number;
    registeredUsers: number;
    newUsers: number;
    topPages: Array<{ path: string; views: number }>;
  };
  cases: {
    open: number;
    opened: number;
    previousOpened: number;
    resolved: number;
    previousResolved: number;
    overdue: number;
    corrections: number;
    averageResolutionHours: number;
    byStatus: Array<{ status: CaseStatus; count: number }>;
    byDepartment: Array<{ department: string; open: number }>;
  };
  payments: {
    collected: number;
    previousCollected: number;
    pendingAmount: number;
    pendingReview: number;
    verifiedTransactions: number;
  };
  services: {
    worksInProgress: number;
    worksPlanned: number;
    publishedNews: number;
    transparencyDocuments: number;
    activeVehicles: number;
    vehiclesOnline: number;
    disabledModules: number;
  };
  daily: Array<{
    date: string;
    opened: number;
    resolved: number;
    views: number;
  }>;
}

export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  createdAt: string;
  read: boolean;
  caseId: string | null;
}

